import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  FormControl,
  Stack
} from '@mui/material';
import LoadingSpinner from '@components/LoadingSpinner';

const ResetPasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    const params = new URLSearchParams(location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type');

    if (!tokenHash || type !== 'recovery') {
      toast.error('Invalid password reset link');
      return;
    }

    setLoading(true);

    try {
      const updateParams = { password: newPassword, token_hash: tokenHash };
      const { error } = await supabase.auth.updateUser(updateParams as { password: string });

      if (error) throw error;

      toast.success('Password updated successfully');

      // Clear God Mode session state to prevent stale impersonation
      sessionStorage.removeItem('impersonation_token');
      sessionStorage.removeItem('impersonated_user');
      sessionStorage.removeItem('godmode_original_user');
      sessionStorage.removeItem('godmode_original_token');

      // Sign out after password reset
      await supabase.auth.signOut();

      // Redirect to home page
      navigate('/');
    } catch (error: unknown) {
      console.error('Error updating password:', error);
      const message = error instanceof Error ? error.message : 'Failed to update password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          Reset Password
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </FormControl>
            <FormControl fullWidth>
              <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </FormControl>
            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
            >
              Update Password
            </Button>
          </Stack>
        </Box>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
